import { useEffect } from 'react';
import { CategoryProductSchema, WebPageSchema } from '@99designs/marketing-ui';
import { vistaI18nRoutesBy99dLocale } from '@99designs/routing';
import { appendUtmParamsToUrl, trackEvent } from '@99designs/tracking';
import { vpProductLandingGroupKeys } from '../../lib/vpProductLandingGroups';
import PageSection from '../PageSection';
import { HeroSection } from './HeroSection';
import { WhyVPSection } from './WhyVPSection';
import type { Site } from '@99designs/site-navigation/lib/Site';
import type { VPProductGroup, VPProductLandingGroupKey } from '../../lib/vpProductLandingGroups';

export interface Props {
    site: Site;
    categoryKey: VPProductLandingGroupKey;
    vpProductGroup: VPProductGroup;
}

export default function VPCategoryPageLayout({ site, categoryKey, vpProductGroup }: Props) {
    const language = site.getLanguageCode();
    const locale = site.siteConfig.locale;
    const ctaLink = appendUtmParamsToUrl(
        vistaI18nRoutesBy99dLocale(locale, vpProductGroup.vpSDPLink),
        {
            utm_source: '99designs',
            utm_medium: 'referral',
            utm_campaign: '99d_business_cards',
        }
    );

    useEffect(() => {
        trackEvent('Viewed Product Page', { category: categoryKey });
    }, [categoryKey]);

    return (
        <>
            <WebPageSchema
                name={vpProductGroup.title}
                description={vpProductGroup.description}
                inLanguage={language}
                mainEntityOfPage={`/${categoryKey}`}
                relatedLink={vpProductLandingGroupKeys.flatMap((k) => [`/${k}`])}
            />
            {vpProductGroup.productSchema ? (
                <CategoryProductSchema {...vpProductGroup.productSchema} />
            ) : null}
            <PageSection id="hero" pageName={categoryKey}>
                <HeroSection
                    categoryKey={categoryKey}
                    price={vpProductGroup.prices[locale]}
                    title={vpProductGroup.heroTitle}
                    features={vpProductGroup.features}
                    introduction={vpProductGroup.introduction}
                    heroImage={vpProductGroup.heroImage}
                    heroCtaAppearance={vpProductGroup.heroCtaAppearance}
                    ctaLink={ctaLink}
                />
            </PageSection>

            <PageSection id="why-vistaprint" pageName={categoryKey}>
                <WhyVPSection title={vpProductGroup.whyVPTitle} image={vpProductGroup.whyVPImage} />
            </PageSection>
        </>
    );
}
